import PropTypes from 'prop-types';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  ArrowLeftIcon,
  EmptyConversationIcon,
  FloatingControl,
  MiniProfile,
  Participants,
  Sheet,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { Stack, Typography } from '@mui/material';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import React, { Suspense, useState } from 'react';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { CountryCode } from '@classity-edtech/platform-utils';
import { Whiteboard as ClsWhiteboard } from '@classity-edtech/ui-base/dist/components/Displays/Whiteboard/Whiteboard';

const TextEditor = React.lazy(() =>
  import('@classity-edtech/ui-base/dist/components/Displays').then(mod => ({
    default: mod.TextEditor,
  })),
);

export interface HomeProps extends MuiBoxProps {}

export const ChatWorkSheet = ({ className }: HomeProps) => {
  const screens = useAppMediaQuery();
  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [showMessages, setShowMessages] = useState(false);
  const [openSheet, setOpenSheet] = useState(false);
  const [content, setContent] = useState('');

  const toggleShowMessages = () => {
    setShowMessages(value => !value);
  };

  const toggleOpenSheet = () => {
    setOpenSheet(value => !value);
  };

  const userProfile = {
    name: `Femi Adewale`,
    src: `https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4`,
    badge: 'Student',
    countryCode: CountryCode.NG,
    bio: '',
    followAction: () => {},
  };

  const participants = {
    groupChat: {
      avatarSrc:
        'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
      title: 'Group - Genius Grove',
      description: 'I can get you to a level of good understanding, no problem!',
      time: '12:25',
      notification: '2',
      isActive: true,
      onClick: toggleShowMessages,
    },
    tutorChat: {
      avatarSrc:
        'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
      title: 'Kemi Balogun',
      description: 'Could you update me on your financial status?',
      time: 'Yesterday',
      isActive: false,
      showActiveIndicator: true,
      onClick: toggleShowMessages,
    },
    peersChat: [
      {
        avatarSrc:
          'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
        title: 'Femi Adewale',
        description: 'Prove that (√3 + 1) (3 – cot 30°) = tan360° – 2 sin 60°',
        time: '2 days ago',
        isActive: false,
        onClick: toggleShowMessages,
      },
      {
        avatarSrc:
          'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
        title: 'Ebegue-Uki Osagie',
        description: 'In the right triangle PQR, Q is right angle.',
        time: '3 days ago',
        isActive: false,
        onClick: toggleShowMessages,
      },
    ],
  };

  const tabs = isSmallScreen
    ? [
        { label: 'Chat', action: () => {}, active: false },
        { label: 'Worksheet #1', action: () => {}, active: true },
      ]
    : [];

  const floatingControls = {
    video: {
      action: () => {},
      isActive: false,
    },
    audio: {
      action: () => {},
      isActive: true,
    },
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {(!isSmallScreen || showMessages) && (
        <Stack
          sx={{
            justifyContent: 'space-between',
            width: '100%',
            maxWidth: '280px',
            position: 'relative',
            borderRight: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <Stack
            sx={{
              height: { xs: 'calc(100dvh - 128px)', md: 'calc(100dvh - 140px)' },
              overflow: 'auto',
              px: { xs: 2.5, md: 1 },
              py: 3,
              gap: 1.5,
              textAlign: 'center',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmptyConversationIcon sx={{ width: '150px', height: 'auto' }} />
            <Typography variant='subtitle5'>Start a conversation!</Typography>
            <Typography variant='body3'>Post a comment below to start the conversation</Typography>
          </Stack>

          <Box
            sx={{
              height: { sm: '46px', md: '140px' },
              px: { xs: 2.5, md: 0 },
              py: { xs: 1, md: 0 },
            }}
          >
            {typeof window !== 'undefined' && (
              <Suspense fallback={<div>Loading editor...</div>}>
                <TextEditor
                  content={content}
                  setContent={setContent}
                  isCompact
                  sendMessage={() => {}}
                />
              </Suspense>
            )}
          </Box>

          <Sheet
            closeSheet={toggleOpenSheet}
            openSheet={openSheet}
            body={
              <Participants sx={{ width: { xs: '100%', md: 'max-content' } }} {...participants} />
            }
          />
        </Stack>
      )}
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: 'min-content',
            borderBottom: `solid 1px ${globalColours['$global-colour-border-neutral']}`,
          }}
        >
          <MiniProfile
            {...userProfile}
            tabs={tabs}
            tabElements={
              <Box sx={{ display: 'flex' }}>
                <Typography
                  variant='body3SemiBold'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: globalColours['$global-colour-interactive-secondary-default'],
                    px: { xs: 1, md: 2 },
                    cursor: 'pointer',
                  }}
                  onClick={isSmallScreen ? toggleOpenSheet : () => {}}
                >
                  <ArrowLeftIcon sx={{ width: '14px', height: '14px' }} />
                  Chat
                </Typography>
                <Typography
                  variant='body3SemiBold'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: globalColours['$global-colour-interactive-secondary-active'],
                    px: { xs: 1, md: 2 },
                    cursor: 'pointer',
                  }}
                >
                  <ArrowLeftIcon
                    sx={{ width: '14px', height: '14px', transform: 'rotate(-90deg)' }}
                  />
                  Worksheet #1
                </Typography>
              </Box>
            }
            moreActions={[{ label: '', action: () => {} }]}
          />
        </Box>

        <ClsWhiteboard
          showClearOption={false}
          showLoadOption={false}
          sx={{
            height: 'calc(100dvh - 120px)',
            width: '100%',
          }}
        />
      </Box>
      <FloatingControl {...floatingControls} />
    </Box>
  );
};

ChatWorkSheet.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

ChatWorkSheet.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(ChatWorkSheet)(
  ({ theme }) => `
`,
);
